import React, { useState, useEffect, useCallback } from 'react';
import { 
  Eye, 
  EyeOff, 
  Copy, 
  RefreshCw, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  X,
  Shield,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

interface PasswordSettings {
  length: number;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  excludeSimilar: boolean;
  excludeAmbiguous: boolean;
}

interface PasswordAnalytics {
  totalGenerated: number;
  averageLength: number;
  mostCommonLength: number;
  strengthDistribution: {
    weak: number;
    medium: number;
    strong: number;
    veryStrong: number;
  };
  generationHistory: Array<{
    timestamp: Date;
    length: number;
    strength: string;
  }>;
}

type PasswordStrength = 'weak' | 'medium' | 'strong' | 'veryStrong';

const PasswordGenerator: React.FC = () => {
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>('weak');

  const [settings, setSettings] = useState<PasswordSettings>({
    length: 16,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeSimilar: false,
    excludeAmbiguous: false
  });

  const [analytics, setAnalytics] = useState<PasswordAnalytics>({
    totalGenerated: 0,
    averageLength: 0,
    mostCommonLength: 16,
    strengthDistribution: { weak: 0, medium: 0, strong: 0, veryStrong: 0 },
    generationHistory: []
  });

  const characters = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
    similar: 'il1Lo0O',
    ambiguous: '{}[]()/\\\'"`~,;.<>'
  };

  const calculatePasswordStrength = useCallback((pwd: string): PasswordStrength => {
    let score = 0;
    
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    if (pwd.length >= 16) score += 1;
    
    if (score <= 2) return 'weak';
    if (score <= 4) return 'medium';
    if (score <= 6) return 'strong';
    return 'veryStrong';
  }, []);

  const generatePassword = useCallback(() => {
    let charset = '';
    
    if (settings.includeUppercase) charset += characters.uppercase;
    if (settings.includeLowercase) charset += characters.lowercase;
    if (settings.includeNumbers) charset += characters.numbers;
    if (settings.includeSymbols) charset += characters.symbols;
    
    if (settings.excludeSimilar) {
      charset = charset.split('').filter(char => !characters.similar.includes(char)).join('');
    }
    
    if (settings.excludeAmbiguous) {
      charset = charset.split('').filter(char => !characters.ambiguous.includes(char)).join('');
    }
    
    if (charset === '') {
      charset = characters.lowercase; // Fallback
    }
    
    let newPassword = '';
    for (let i = 0; i < settings.length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    setPassword(newPassword);
    const strength = calculatePasswordStrength(newPassword);
    setPasswordStrength(strength);
    
    // Update analytics
    setAnalytics(prev => {
      const newHistory = [...prev.generationHistory, {
        timestamp: new Date(),
        length: settings.length,
        strength
      }].slice(-100); // Keep last 100 generations
      
      const totalGenerated = prev.totalGenerated + 1;
      const avgLength = (prev.averageLength * prev.totalGenerated + settings.length) / totalGenerated;
      
      const strengthDist = { ...prev.strengthDistribution };
      strengthDist[strength]++;
      
      return {
        totalGenerated,
        averageLength: Math.round(avgLength * 10) / 10,
        mostCommonLength: settings.length,
        strengthDistribution: strengthDist,
        generationHistory: newHistory
      };
    });
  }, [settings, calculatePasswordStrength]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy password');
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getStrengthColor = (strength: PasswordStrength): string => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-blue-500';
      case 'veryStrong': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStrengthText = (strength: PasswordStrength): string => {
    switch (strength) {
      case 'weak': return 'Weak';
      case 'medium': return 'Medium';
      case 'strong': return 'Strong';
      case 'veryStrong': return 'Very Strong';
      default: return 'Unknown';
    }
  };

  useEffect(() => {
    generatePassword();
  }, []);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
              <div className="flex items-center space-x-3">
                <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                  Password Generator
                </h1>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={toggleFullscreen}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Password Generator */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <div className="space-y-6">
                  {/* Password Display */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Generated Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        readOnly
                        className="w-full p-4 pr-24 text-lg font-mono bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          title={showPassword ? "Hide" : "Show"}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                        <button
                          onClick={copyToClipboard}
                          className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          title="Copy"
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Password Strength */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Password Strength: {getStrengthText(passwordStrength)}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {password.length} characters
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength)}`}
                        style={{
                          width: passwordStrength === 'weak' ? '25%' :
                                 passwordStrength === 'medium' ? '50%' :
                                 passwordStrength === 'strong' ? '75%' : '100%'
                        }}
                      />
                    </div>
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={generatePassword}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <RefreshCw className="w-5 h-5" />
                    <span>Generate New Password</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Settings
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Length */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Length: {settings.length}
                      </label>
                      <input
                        type="range"
                        min="4"
                        max="128"
                        value={settings.length}
                        onChange={(e) => setSettings(prev => ({ ...prev, length: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                      />
                    </div>

                    {/* Character Options */}
                    <div className="space-y-3">
                      {[
                        { key: 'includeUppercase', label: 'Uppercase (A-Z)' },
                        { key: 'includeLowercase', label: 'Lowercase (a-z)' },
                        { key: 'includeNumbers', label: 'Numbers (0-9)' },
                        { key: 'includeSymbols', label: 'Symbols (!@#$...)' },
                        { key: 'excludeSimilar', label: 'Exclude Similar (il1Lo0O)' },
                        { key: 'excludeAmbiguous', label: 'Exclude Ambiguous ({}[]()...)' }
                      ].map(({ key, label }) => (
                        <label key={key} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings[key as keyof PasswordSettings] as boolean}
                            onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Analytics
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.totalGenerated}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Generated
                        </div>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {analytics.averageLength}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Length
                        </div>
                      </div>
                    </div>

                    {/* Strength Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Strength Distribution
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.strengthDistribution).map(([strength, count]) => (
                          <div key={strength} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400 capitalize">
                              {strength === 'veryStrong' ? 'Very Strong' : strength}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent History */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Recent History
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {analytics.generationHistory.slice(-5).reverse().map((entry, index) => (
                          <div key={index} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                            <span>{entry.length} chars</span>
                            <span className="capitalize">{entry.strength === 'veryStrong' ? 'V.Strong' : entry.strength}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordGenerator;